import { call, put, takeLatest, takeLeading } from 'redux-saga/effects';
import { getWithCredentials, postWithCredentials } from 'api';
import { EXCHANGE_TOKEN_FEATURE } from 'constants/features';
import { PODCASTER_PORTAL_ENCRYPTED_DEVICE_TYPE_ID } from 'constants/metrics';
import {
  EXCHANGE_TOKEN_API,
  GET_USER_PREFERENCE_AUTH_API,
  GET_USER_PREFERENCE_UNAUTH_API,
  UPDATE_USER_PREFERENCE_AUTH_API,
  UPDATE_USER_PREFERENCE_UNAUTH_API,
  USER_INFO_API,
} from 'constants/routes';
import { initialize } from 'store/slices/initialize';
import { checkFeedClaims } from 'store/slices/feed';
import {
  authenticated,
  updateAuth,
  updateUserInfo,
  updateCookiePreference,
  updateUserCookiePreference,
} from 'store/slices/user';
import { checkForFeature } from 'utils/features';
import { updateSession } from 'utils/session';
import { getQueryStringParams, queryStringWithoutCSRF } from 'utils/url';
import Cookies from 'js-cookie';

export function* exchangeToken(): any {
  const response = yield call(postWithCredentials, {
    path: EXCHANGE_TOKEN_API,
    body: {
      deviceType: PODCASTER_PORTAL_ENCRYPTED_DEVICE_TYPE_ID,
    },
  });

  if (response.ok) {
    const { token, expirationTimeInMilliseconds } = yield call([
      response,
      'json',
    ]);
    yield put(
      updateAuth({ token, expirationTime: expirationTimeInMilliseconds })
    );
    return token;
  }
}

function* auth(): any {
  try {
    const response = yield call(getWithCredentials, {
      path: USER_INFO_API,
    });

    if (response.ok) {
      const json = yield call([response, 'json']);

      yield put(updateUserInfo(json));
      yield put(authenticated(true));

      if (checkForFeature(EXCHANGE_TOKEN_FEATURE)) {
        yield call(exchangeToken);
      }
      return;
    }
  } catch (error) {
    yield put(authenticated(false));
    yield put(updateUserInfo({ hasFeedClaims: false }));
  }
  yield put(authenticated(false));
  yield put(updateUserInfo({ hasFeedClaims: false }));
}

function checkCsrf() {
  const [csrfToken] = getQueryStringParams(['csrf-token']);

  if (csrfToken) {
    const cleanToken = csrfToken.replace(new RegExp(' ', 'g'), '+');
    localStorage.setItem('csrfToken', cleanToken);

    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${queryStringWithoutCSRF()}`
    );
  }
}

export function* getCookiePreference(): any {
  const cookieConsent = sessionStorage.getItem('cookieConsent');

  if (!cookieConsent) {
    const GET_USER_PREFERENCE_API_PATH = Cookies.get('x-main')
      ? GET_USER_PREFERENCE_AUTH_API
      : GET_USER_PREFERENCE_UNAUTH_API;

    try {
      const response = yield call(getWithCredentials, {
        path: GET_USER_PREFERENCE_API_PATH,
      });

      if (response.ok) {
        const json = yield call([response, 'json']);

        yield put(updateCookiePreference(json));
        sessionStorage.setItem('cookieConsent', json.cookieConsent);
      } else {
        sessionStorage.setItem('cookieConsent', 'NONE');
      }
    } catch (error) {
      sessionStorage.setItem('cookieConsent', 'NONE');
    }
  }
}

export function* setCookiePreference(action: {
  payload: { preference: string };
  type: string;
}): any {
  const preference = action.payload.preference;
  const UPDATE_USER_PREFERENCE_API_PATH = Cookies.get('x-main')
    ? UPDATE_USER_PREFERENCE_AUTH_API
    : UPDATE_USER_PREFERENCE_UNAUTH_API;

  yield call(postWithCredentials, {
    path: UPDATE_USER_PREFERENCE_API_PATH,
    body: { cookieConsent: preference },
  });

  yield put(updateCookiePreference({ cookieConsent: preference }));
  sessionStorage.setItem('cookieConsent', preference);
}

function initalizeSessionListener() {
  document.body.addEventListener('click', updateSession, { passive: true });
  document.body.addEventListener('musicActivate', updateSession, {
    passive: true,
  });
  document.body.addEventListener('change', updateSession, { passive: true });
}

export const initializeSagas = [
  takeLeading(initialize.toString(), auth),
  takeLeading(initialize.toString(), checkCsrf),
  takeLeading(initialize.toString(), updateSession),
  takeLeading(initialize.toString(), initalizeSessionListener),
  takeLeading(initialize.toString(), getCookiePreference),
  takeLatest(checkFeedClaims.toString(), auth),
  takeLatest(updateUserCookiePreference.toString(), setCookiePreference),
];
